/**
 * Amount analysis for recurring transaction detection
 * Handles amount consistency checking and price change detection
 */

import { average } from '../../utils/array-helpers';
import { DETECTION_CONFIG } from './config';

/**
 * Result of amount analysis
 */
export interface AmountAnalysis {
  /** Whether amounts are consistent enough to be recurring */
  isConsistent: boolean;

  /** Average amount across all transactions */
  avgAmount: number;

  /** Minimum amount */
  minAmount: number;

  /** Maximum amount */
  maxAmount: number;

  /** Number of unique price points */
  uniquePriceCount: number;

  /** Whether this looks like price increases vs random variance */
  hasPriceChanges: boolean;

  /** Reason for consistency decision (for debugging) */
  reason: string;
}

/**
 * Analyze transaction amounts for consistency
 * Distinguishes between legitimate price changes and random variance
 *
 * @param amounts - Array of transaction amounts (absolute values)
 * @returns Comprehensive amount analysis
 */
export function analyzeAmounts(amounts: number[]): AmountAnalysis {
  if (amounts.length === 0) {
    return {
      isConsistent: false,
      avgAmount: 0,
      minAmount: 0,
      maxAmount: 0,
      uniquePriceCount: 0,
      hasPriceChanges: false,
      reason: 'No amounts provided'
    };
  }

  const avgAmount = average(amounts);
  const minAmount = Math.min(...amounts);
  const maxAmount = Math.max(...amounts);

  // Group amounts by unique values
  const amountGroups = groupAmounts(amounts);
  const uniquePriceCount = amountGroups.length;

  // Analyze consistency based on unique price count
  if (uniquePriceCount === 1) {
    // Perfect: all same amount
    return {
      isConsistent: true,
      avgAmount,
      minAmount,
      maxAmount,
      uniquePriceCount,
      hasPriceChanges: false,
      reason: 'All amounts identical'
    };
  }

  if (uniquePriceCount === 2) {
    // Two different amounts - check if it's a price change
    const result = analyzeTwoPricePoints(amountGroups, avgAmount, minAmount, maxAmount);
    return {
      ...result,
      uniquePriceCount,
      avgAmount,
      minAmount,
      maxAmount
    };
  }

  if (uniquePriceCount <= 3) {
    // Up to 3 different amounts - check for gradual increases
    const result = analyzeGradualPriceChanges(amountGroups, avgAmount, minAmount, maxAmount);
    return {
      ...result,
      uniquePriceCount,
      avgAmount,
      minAmount,
      maxAmount
    };
  }

  // Many different amounts - fall back to traditional tolerance check
  const result = analyzeWithTolerance(amounts, avgAmount, minAmount, maxAmount);
  return {
    ...result,
    uniquePriceCount,
    avgAmount,
    minAmount,
    maxAmount
  };
}

/**
 * Check if two amounts are similar (within tolerance)
 */
export function amountsSimilar(
  amount1: number,
  amount2: number,
  tolerance: number = DETECTION_CONFIG.thresholds.amountTolerance
): boolean {
  const diff = Math.abs(amount1 - amount2);
  const avg = (Math.abs(amount1) + Math.abs(amount2)) / 2;
  return diff / avg <= tolerance;
}

/**
 * Group amounts and sort by value
 * Returns unique amounts with their frequency
 */
function groupAmounts(amounts: number[]): Array<{ amount: number; count: number }> {
  const groups = new Map<string, number>();

  amounts.forEach(amt => {
    const key = amt.toFixed(2);
    groups.set(key, (groups.get(key) || 0) + 1);
  });

  return Array.from(groups.entries())
    .map(([amtStr, count]) => ({ amount: parseFloat(amtStr), count }))
    .sort((a, b) => a.amount - b.amount);
}

/**
 * Analyze pattern with exactly 2 unique price points
 * Likely a subscription price increase
 *
 * @example
 * Netflix: $15.49 (3x) → $17.99 (4x) = 16% increase, should pass
 */
function analyzeTwoPricePoints(
  groups: Array<{ amount: number; count: number }>,
  avgAmount: number,
  minAmount: number,
  maxAmount: number
): Omit<AmountAnalysis, 'avgAmount' | 'minAmount' | 'maxAmount' | 'uniquePriceCount'> {
  const [price1, price2] = groups.map(g => g.amount);
  const priceIncrease = Math.abs(price2 - price1) / Math.min(price1, price2);
  const threshold = DETECTION_CONFIG.thresholds.priceIncreaseThreshold;

  if (priceIncrease <= threshold) {
    return {
      isConsistent: true,
      hasPriceChanges: true,
      reason: `Price change from $${price1.toFixed(2)} to $${price2.toFixed(2)} (${(priceIncrease * 100).toFixed(1)}% increase) is within ${threshold * 100}% threshold`
    };
  }

  return {
    isConsistent: false,
    hasPriceChanges: false,
    reason: `Price change from $${price1.toFixed(2)} to $${price2.toFixed(2)} (${(priceIncrease * 100).toFixed(1)}% increase) exceeds ${threshold * 100}% threshold`
  };
}

/**
 * Analyze pattern with 3 unique price points
 * Check if increases are gradual and reasonable
 *
 * @example
 * Spotify: $9.99 → $10.99 → $11.99 (gradual increases, should pass)
 * Random: $10.00 → $5.00 → $15.00 (random variance, should fail)
 */
function analyzeGradualPriceChanges(
  groups: Array<{ amount: number; count: number }>,
  avgAmount: number,
  minAmount: number,
  maxAmount: number
): Omit<AmountAnalysis, 'avgAmount' | 'minAmount' | 'maxAmount' | 'uniquePriceCount'> {
  const amounts = groups.map(g => g.amount);
  const threshold = DETECTION_CONFIG.thresholds.priceIncreaseThreshold;

  // Check each consecutive price change
  for (let i = 1; i < amounts.length; i++) {
    const increase = (amounts[i] - amounts[i - 1]) / amounts[i - 1];

    // Price decrease = not a subscription pattern
    if (increase < 0) {
      return {
        isConsistent: false,
        hasPriceChanges: false,
        reason: `Price decreased from $${amounts[i - 1].toFixed(2)} to $${amounts[i].toFixed(2)}`
      };
    }

    // Price increase too large
    if (increase > threshold) {
      return {
        isConsistent: false,
        hasPriceChanges: false,
        reason: `Price increase from $${amounts[i - 1].toFixed(2)} to $${amounts[i].toFixed(2)} (${(increase * 100).toFixed(1)}%) exceeds ${threshold * 100}% threshold`
      };
    }
  }

  return {
    isConsistent: true,
    hasPriceChanges: true,
    reason: `Gradual price increases from $${amounts[0].toFixed(2)} to $${amounts[amounts.length - 1].toFixed(2)} are within threshold`
  };
}

/**
 * Analyze amounts using traditional tolerance check
 * Used when there are many unique amounts
 */
function analyzeWithTolerance(
  amounts: number[],
  avgAmount: number,
  minAmount: number,
  maxAmount: number
): Omit<AmountAnalysis, 'avgAmount' | 'minAmount' | 'maxAmount' | 'uniquePriceCount'> {
  const tolerance = DETECTION_CONFIG.thresholds.amountTolerance;
  const allWithinTolerance = amounts.every(amount =>
    amountsSimilar(amount, avgAmount, tolerance)
  );

  if (allWithinTolerance) {
    return {
      isConsistent: true,
      hasPriceChanges: false,
      reason: `All amounts within ${tolerance * 100}% of average $${avgAmount.toFixed(2)}`
    };
  }

  return {
    isConsistent: false,
    hasPriceChanges: false,
    reason: `Too much variance: amounts range from $${minAmount.toFixed(2)} to $${maxAmount.toFixed(2)} with average $${avgAmount.toFixed(2)}`
  };
}
